
import { toast } from 'sonner';
import { BaseLogger } from './BaseLogger';
import { ConsoleLogger } from './ConsoleLogger';
import { LogStorageService } from './LogStorageService';
import { LogEntry, LogLevel, LogQueryOptions, LogQueryResult } from './types';

/**
 * Singleton LoggingService class that combines console and persistent storage
 */
class LoggingService extends BaseLogger {
  private static instance: LoggingService;
  private consoleLogger: ConsoleLogger;
  private storageService: LogStorageService;

  private constructor() {
    super();
    this.consoleLogger = new ConsoleLogger();
    this.storageService = new LogStorageService();
  }

  public static getInstance(): LoggingService {
    if (!LoggingService.instance) {
      LoggingService.instance = new LoggingService();
    }
    return LoggingService.instance;
  }

  /**
   * Log a message with specified level
   */
  public async log(
    level: LogLevel,
    message: string,
    details?: any,
    source?: string
  ): Promise<void> {
    try {
      const logEntry: LogEntry = {
        level,
        message,
        details: details ? details : null,
        source: source || 'application',
        user_id: this.userId || undefined
      };

      // Log to console first (for immediate feedback)
      this.consoleLogger.logToConsole(logEntry);

      // Store in Supabase
      await this.storageService.storeLog(logEntry);
      
      // Show toast for fatal errors
      if (level === 'fatal') {
        toast.error(`Fatal error: ${message}`);
      }
    } catch (error) {
      // Fallback to console in case logging itself fails
      console.error('Logging service error:', error);
    }
  }

  /**
   * Fetch logs with pagination and filtering
   */
  public async getLogs(options: LogQueryOptions = {}): Promise<LogQueryResult> {
    return this.storageService.getLogs(options);
  }
}

export default LoggingService.getInstance();
export * from './types';
